import FieldHtmlNameUtil from '../../model/hekate/FieldHtmlNameUtil';
import Trans from '../../lib/translations/Trans';
import FormValidationResult from './FormValidationResults';
import ReservedWords from './ReservedWords';
/** Level 1 Validations used for quick validation completely client side that do not required the database or similar. */
export default class L1Validations {
    /** Validate a single given field. */
    static validateSingleField({ field, index, }) {
        var _a;
        const fieldName = field.htmlName;
        const size = L1Validations.validateMaxSize({
            field,
            index,
        });
        if (size) {
            return size;
        }
        // check for field being required
        if (field.isRequired && (field.val === '' || field.val === false)) {
            return {
                index,
                htmlName: field.htmlName,
                message: Trans.byKey('validationRequiredField'),
                name: field.name,
                hint: 'Required',
            };
        }
        // variable validations
        const validationResults = ((_a = field.validations) !== null && _a !== void 0 ? _a : []).map((condition) => {
            switch (condition.name) {
                case 'IsRequiredCondition':
                    return L1Validations.validateNonEmpty(field, index);
                case 'IsEmailAddressCondition':
                    return L1Validations.validateEmail(field, index);
                case 'PreventUrlCondition':
                    return L1Validations.validateNoURL(field, index);
                case 'PreventXSSCondition':
                    return L1Validations.validateNoHTML(field, index);
                case 'unsupported':
                    return null;
                default:
                    return null;
            }
        });
        const firstresult = validationResults.find((result) => result);
        if (firstresult) {
            return firstresult;
        }
        // hard-coded validations
        switch (fieldName) {
            case FieldHtmlNameUtil.firstName:
            case FieldHtmlNameUtil.lastName:
                return L1Validations.validatePersonsName(field, index);
            case FieldHtmlNameUtil.email:
            case FieldHtmlNameUtil.referredEmail:
            case FieldHtmlNameUtil.addEmail1:
            case FieldHtmlNameUtil.addEmail2:
            case FieldHtmlNameUtil.addEmail3:
            case FieldHtmlNameUtil.addEmail4:
            case FieldHtmlNameUtil.addEmail5:
                return L1Validations.validateEmail(field, index);
            case FieldHtmlNameUtil.companyName:
            case FieldHtmlNameUtil.classroomName:
                return L1Validations.validateCompanyName(field, index);
            case FieldHtmlNameUtil.jobTitle:
                return L1Validations.validateRepeatChars(field, index);
            case FieldHtmlNameUtil.phoneNumber:
                return L1Validations.validatePhoneNumber(field, index);
            case FieldHtmlNameUtil.ageVerification:
                return L1Validations.validateAgeVerification(field, index);
            default:
                return null;
        }
    }
    /** Check that the form is valid (e.g on clicking submit) */
    static checkValidation({ fields, }) {
        const result = new FormValidationResult();
        L1Validations.validateAll({
            fields,
            result,
        });
        L1Validations.conditionalValidateAll({
            fields,
            result,
        });
        return result;
    }
    /** Validate/clean a single field as when the "blur" event is triggered */
    static blurSingleField({ field, }) {
        if (typeof field.val === 'string' && field.val.trim() !== field.val) {
            field.val = field.val.trim();
        }
        const error = L1Validations.validateSingleField({ field, index: -1 });
        return Promise.resolve(error);
    }
    /** Validate firstName or lastName or similar. */
    static validatePersonsName(field, index) {
        const includesRepeatChars = L1Validations.validateRepeatChars(field, index);
        const includesReservedName = L1Validations.nameMatchesReserved(field, index);
        return includesRepeatChars !== null && includesRepeatChars !== void 0 ? includesRepeatChars : includesReservedName;
    }
    /** Validate that a field is not empty. */
    static validateNonEmpty(field, index) {
        if (field.val === '' || field.val === false) {
            return {
                index,
                htmlName: field.htmlName,
                message: Trans.byKey('validationRequiredField'),
                name: field.name,
                hint: 'Required',
            };
        }
        return null;
    }
    /** Validate that there are not too many repeat chars. */
    static validateRepeatChars(field, index) {
        const { val, htmlName, name, isRequired } = field;
        if (typeof val !== 'string') {
            return null;
        }
        if (!isRequired && val === '') {
            return null;
        }
        let hint = '';
        let isErrorFound = false;
        if (/(.)\1\1\1/.test(val)) {
            isErrorFound = true;
            hint = 'Single char repeat';
        }
        if (/(..)\1\1/.test(val)) {
            isErrorFound = true;
            hint = 'Double char repeat';
        }
        if (/(...)\1/.test(val) && !/^[A-Z]/.test(val)) {
            isErrorFound = true;
            hint = 'Triple char repeat';
        }
        if (!isErrorFound) {
            return null;
        }
        return {
            htmlName,
            hint,
            name,
            index,
            message: Trans.byKey('validationRepeatPatternFound'),
        };
    }
    /** Validate that the email has a '@' and the hostname has a '.' */
    static validateEmail(field, index) {
        const { val, htmlName, name, isRequired } = field;
        if (typeof val !== 'string') {
            return null;
        }
        if (!isRequired && val === '') {
            return null;
        }
        if (L1Validations.includesCommonBadEmailChars(val)) {
            return {
                htmlName,
                name,
                index,
                message: Trans.byKey('validationInvalidEmail'),
                hint: 'bad char',
            };
        }
        if (val.includes('..')) {
            return {
                htmlName,
                name,
                index,
                message: Trans.byKey('validationInvalidEmail'),
                hint: 'No double period',
            };
        }
        // fail if includes whitespace
        if (/\s/.exec(val)) {
            return {
                htmlName,
                name,
                index,
                message: Trans.byKey('validationInvalidEmail'),
                hint: 'Found whitespace',
            };
        }
        let bits = val.split('@');
        if (bits.length !== 2) {
            return {
                htmlName,
                name,
                index,
                message: Trans.byKey('validationInvalidEmail'),
                hint: 'Missing @',
            };
        }
        bits = bits[1].split('.');
        if (bits.length === 1) {
            return {
                htmlName,
                name,
                index,
                message: Trans.byKey('validationInvalidEmail'),
                hint: 'No . in hostname',
            };
        }
        const tld = bits.pop();
        if (tld && tld.length < 2) {
            return {
                htmlName,
                name,
                index,
                message: Trans.byKey('validationInvalidEmail'),
                hint: 'TLD too short.',
            };
        }
        if (val.endsWith('.') || val.startsWith('.')) {
            return {
                htmlName,
                name,
                index,
                message: Trans.byKey('validationInvalidEmail'),
                hint: 'starts/ends with .',
            };
        }
        if (val.includes('.@') || val.includes('@.')) {
            return {
                htmlName,
                name,
                index,
                message: Trans.byKey('validationInvalidEmail'),
                hint: 'Found .@',
            };
        }
        // Check for multiple consecutive pluses, except for whitelisted domains
        const emailParts = val.split('@');
        const domain = emailParts[1].toLowerCase();
        const isUnsupportedDomain = ['qq.com', '163.com'];
        if (isUnsupportedDomain.includes(domain)) {
            return {
                htmlName,
                name,
                index,
                message: Trans.byKey('validationUnsupportedEmailProvider'),
                hint: 'Unsupported domain',
            };
        }
        const isWhitelistedDomain = domain === 'ptc.com' || domain === 'onshape.com';
        if (!isWhitelistedDomain && val.includes('++')) {
            return {
                htmlName,
                name,
                index,
                message: Trans.byKey('validationInvalidEmail'),
                hint: 'Multiple pluses not allowed',
            };
        }
        return null;
    }
    /** Return true if a commonly typed bad char is present in email */
    static includesCommonBadEmailChars(email) {
        // chars that are not accepted by Eloqua in emails
        const isCommonBadEmailChars = ['/', ',', '=', '~', ' ', '#', ';', '\\', '%'];
        const found = isCommonBadEmailChars.find((ch) => email.includes(ch));
        return !!found;
    }
    /** Validate a phone number against a regex */
    static validatePhoneNumber(field, index) {
        const { val, htmlName, name, isRequired } = field;
        if (typeof val !== 'string') {
            return null;
        }
        if (!isRequired && val === '') {
            return null;
        }
        const phoneRegex = /^\+?[0-9() -]{8,20}$/;
        if (phoneRegex.test(val)) {
            return null;
        }
        return {
            htmlName,
            name,
            index,
            message: Trans.byKey('validationInvalidPhoneNumber'),
            hint: 'Phone number did not match regex.',
        };
    }
    /** Validate that no URL is present in the input */
    static validateNoURL(field, index) {
        const { val, htmlName, name } = field;
        if (typeof val !== 'string') {
            return null;
        }
        if (/https?:\/\//.exec(val)) {
            return {
                htmlName,
                name,
                index,
                message: Trans.byKey('validationContainsURL'),
                hint: 'This field cannot contain a URL',
            };
        }
        return null;
    }
    /** Validate that no HTML characters are present */
    static validateNoHTML(field, index) {
        const { val, htmlName, name, isRequired } = field;
        if (typeof val !== 'string') {
            return null;
        }
        if (!isRequired && val === '') {
            return null;
        }
        if (val.includes('<')) {
            return {
                htmlName,
                name,
                index,
                message: Trans.byKey('validationContainsHTML'),
                hint: 'This field cannot contain <',
            };
        }
        if (val.includes('>')) {
            return {
                htmlName,
                name,
                index,
                message: Trans.byKey('validationContainsHTML'),
                hint: 'This field cannot contain >',
            };
        }
        if (val.includes('&')) {
            return {
                htmlName,
                name,
                index,
                message: Trans.byKey('validationContainsHTML'),
                hint: 'This field cannot contain &',
            };
        }
        return null;
    }
    /** Validate all fields 1 by 1 */
    static validateAll({ fields, result, }) {
        fields.forEach((field, index) => {
            const error = L1Validations.validateSingleField({
                field,
                index,
            });
            if (error === null) {
                return;
            }
            result.pushFieldError(error);
        });
    }
    /**
     * Make sure the first and last name are not short and match. e.g. aaa aaa
     * should fail.
     */
    static conditionalValidateFirstNameAndLastName({ fields, result, }) {
        const firstNameIndex = L1Validations.findFieldIndexByName(FieldHtmlNameUtil.firstName, fields);
        const lastNameIndex = L1Validations.findFieldIndexByName(FieldHtmlNameUtil.lastName, fields);
        if (firstNameIndex === -1 || lastNameIndex === -1) {
            return null;
        }
        const firstName = fields[firstNameIndex];
        const lastName = fields[lastNameIndex];
        if (typeof firstName.val !== 'string' || typeof lastName.val !== 'string') {
            console.warn('wrong type for firstName or lastName');
            return null;
        }
        if (firstName.val.length === 0 || lastName.val.length === 0) {
            return null;
        }
        if (firstName.val.length < 4 && firstName.val === lastName.val) {
            result.pushFieldError({
                index: firstNameIndex,
                htmlName: firstName.htmlName,
                name: firstName.name,
                message: Trans.byKey('validationBadFirstNameAndLastNameCombo'),
                hint: 'Short first and last name matches.',
            });
            result.pushFieldError({
                index: lastNameIndex,
                htmlName: lastName.htmlName,
                name: lastName.name,
                message: Trans.byKey('validationBadFirstNameAndLastNameCombo'),
                hint: 'Short first and last name matches.',
            });
            return {
                message: Trans.byKey('validationBadFirstNameAndLastNameCombo'),
                hint: 'Short first and last name matches.',
            };
        }
        if (L1Validations.fullNameIncludesReserved({ firstName, lastName })) {
            result.pushFieldError({
                index: firstNameIndex,
                htmlName: firstName.htmlName,
                message: Trans.byKey('validationBadFirstNameAndLastNameCombo'),
                hint: 'Full Name includes reserved word',
                name: firstName.name,
            });
            result.pushFieldError({
                index: lastNameIndex,
                htmlName: lastName.htmlName,
                message: Trans.byKey('validationBadFirstNameAndLastNameCombo'),
                hint: 'Full Name includes reserved word',
                name: lastName.name,
            });
            return {
                message: Trans.byKey('validationBadFirstNameAndLastNameCombo'),
                hint: 'Full Name includes reserved word',
            };
        }
        return null;
    }
    static validateCompanyName(field, index) {
        const repeats = L1Validations.validateRepeatChars(field, index);
        if (repeats) {
            return repeats;
        }
        const { val, htmlName, name, isRequired } = field;
        if (typeof val !== 'string') {
            return null;
        }
        if (!isRequired && val === '') {
            return null;
        }
        let hint = '';
        let isErrorFound = false;
        if (L1Validations.isReservedCompanyName({ field })) {
            hint = 'Invalid company name';
            isErrorFound = true;
        }
        if (!isErrorFound) {
            return null;
        }
        return {
            htmlName,
            hint,
            name,
            index,
            message: Trans.byKey('invalidCompanyName'),
        };
    }
    static validateAgeVerification(field, index) {
        const { val } = field;
        if (typeof val === 'string' && (val === 'underage' || val === 'overage' || val === '')) {
            return null;
        }
        return {
            htmlName: field.htmlName,
            name: field.name,
            index,
            message: Trans.byKey('invalidDateFormat'),
            hint: 'Invalid date format',
        };
    }
    /** Get the index of a field by its `htmlName` */
    static findFieldIndexByName(htmlName, fields) {
        const found = fields.findIndex((field) => field.htmlName === htmlName);
        return found;
    }
    /** Validate all fields that require more than one field validate. */
    static conditionalValidateAll({ fields, result, }) {
        const error = L1Validations.conditionalValidateFirstNameAndLastName({
            fields,
            result,
        });
        if (error) {
            result.pushFormError(error);
        }
    }
    static validateMaxSize({ field, index, }) {
        const contentLength = new Blob([`${field.val}`]).size;
        const maxLength = L1Validations.maxLengthByField(field);
        if (contentLength < maxLength)
            return null;
        const msg = Trans.byKey('inputLengthExceedsMaxLength');
        return {
            index,
            htmlName: field.htmlName,
            message: msg,
            name: field.name,
            hint: 'too long input',
        };
    }
    /** Given a field return max length for that field, this also sets the default */
    static maxLengthByField(field) {
        const defaultMaxSize = 10000;
        switch (field.htmlName) {
            case FieldHtmlNameUtil.firstName:
            case FieldHtmlNameUtil.lastName:
            case FieldHtmlNameUtil.email:
                return 256;
            case FieldHtmlNameUtil.phoneNumber:
                return 32;
            case FieldHtmlNameUtil.companyName:
            case FieldHtmlNameUtil.classroomName:
                return 64;
            default:
                return defaultMaxSize;
        }
    }
    // see: /util/BTReservedNames.properties in the product github codebase
    static isReservedCompanyName({ field }) {
        const lcVal = `${field.val}`.toLocaleLowerCase();
        // return exact match only
        return !!ReservedWords.reservedCompanyNames.find((invalid) => invalid === lcVal);
    }
    static fullNameIncludesReserved({ firstName, lastName, }) {
        const lcFirstName = `${firstName.val}`.toLocaleLowerCase();
        const lcLastName = `${lastName.val}`.toLocaleLowerCase();
        const lcFullName = `${lcFirstName}${lcLastName}`;
        let found = '';
        ReservedWords.reservedFirstLastNames.forEach((invalid) => {
            if (lcFullName.includes(invalid)) {
                found = invalid;
            }
        });
        return !!found;
    }
    static nameMatchesReserved(field, index) {
        const { val, htmlName, name, isRequired } = field;
        if (typeof val !== 'string') {
            return null;
        }
        if (!isRequired && val === '') {
            return null;
        }
        const lcFieldVal = `${field.val}`.toLocaleLowerCase();
        if (ReservedWords.reservedFirstLastNames.find((invalid) => invalid === lcFieldVal)) {
            return {
                htmlName,
                hint: 'Name field includes reserved word',
                name,
                index,
                message: Trans.byKey('validationBadFirstNameAndLastNameCombo'),
            };
        }
        return null;
    }
}
